/**
 * Reporte general de Pagos / Liquidación de Facturas.
 *
 * Puerto de `Credito::report_pagos` + `views/menu/credito/report_pagos.php`: es
 * el reporte que el listado de créditos del panel anterior abría con el botón
 * «Reporte de pagos» de la cabecera. Muestra las **facturas a crédito de todos
 * los clientes** del rango, agrupadas por cliente, con los totales de facturado,
 * retención, abonado y pendiente, y el estado de cada factura.
 *
 * Qué se porta:
 * - Los tres indicadores del original: **Total Facturado**, **Total Abonado** y
 *   **Total Pendiente**.
 * - La tabla agrupada por cliente con su **subtotal** y las columnas Emisión,
 *   Vencimiento, Factura, Información, Total, Retención, Abonado, Pendiente,
 *   Vencido y Estado.
 * - El **estado** de cada factura (`_estado_factura`): `Pagado` si ya no queda
 *   saldo, `Vencido` si su vencimiento pasó, `Por vencer` dentro de los próximos
 *   siete días y `Vigente` el resto. El filtro se aplica en el servidor, como el
 *   original.
 * - El **PDF horizontal** (`Credito::report_pagos_pdf`), compuesto por la API con
 *   su propio escritor de PDF.
 */

import { BadgeDollarSign, FileDown, HandCoins, Hourglass, ReceiptText } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Select } from '@/components/ui/select';
import { ApiError } from '@/lib/http/api-error';

import { cierreApi, useReportePagos } from '../cierre.api';
import type { EstadoFacturaCredito, FiltrosReportePagos } from '../cierre-gestion.types';
import { fechaCorta, hoyIso, monto } from '../gestion-formato';

const INICIO_POR_DEFECTO = '2022-09-02';

/** Opciones del filtro de estado (las del `<Select>` del original). */
const ESTADOS: { valor: string; etiqueta: string }[] = [
  { valor: 'todos', etiqueta: 'Todos' },
  { valor: 'Pagado', etiqueta: 'Pagados' },
  { valor: 'Vencido', etiqueta: 'Vencidos' },
  { valor: 'Por vencer', etiqueta: 'Por vencer' },
  { valor: 'Vigente', etiqueta: 'Vigentes' },
];

/** Tonos del distintivo según el estado de la factura. */
const TONOS: Record<EstadoFacturaCredito, 'success' | 'danger' | 'warning' | 'neutral'> = {
  Pagado: 'success',
  Vencido: 'danger',
  'Por vencer': 'warning',
  Vigente: 'neutral',
};

const CONTROL =
  'h-11 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

interface Props {
  /** El diálogo se muestra cuando es `true`. */
  open: boolean;
  onClose: () => void;
}

export function CreditoReportePagosDialog({ open, onClose }: Props) {
  const [filtros, setFiltros] = useState<FiltrosReportePagos>(() => ({
    desde: INICIO_POR_DEFECTO,
    hasta: hoyIso(),
    estado: 'todos',
  }));
  const [aplicados, setAplicados] = useState<FiltrosReportePagos>(filtros);
  const [errorPdf, setErrorPdf] = useState<string | null>(null);
  const [descargando, setDescargando] = useState(false);

  const consulta = useReportePagos(aplicados, open);

  const datos = consulta.data;
  const resumen = datos?.resumen;

  const totales = useMemo(
    () => ({
      facturado: resumen?.facturado ?? 0,
      abonado: resumen?.abonado ?? 0,
      pendiente: resumen?.pendiente ?? 0,
    }),
    [resumen],
  );

  /** Descarga el PDF del reporte con los filtros aplicados. */
  const descargarPdf = async () => {
    setErrorPdf(null);
    setDescargando(true);

    try {
      await cierreApi.pdfReportePagos(aplicados);
    } catch (fallo) {
      setErrorPdf(fallo instanceof Error ? fallo.message : 'No se pudo generar el PDF.');
    } finally {
      setDescargando(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="xl"
      title="Reporte general · Pagos / Liquidación de facturas"
      description="Facturas a crédito de todos los clientes del rango, agrupadas por cliente."
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cerrar
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={!datos}
            loading={descargando}
            leftIcon={<FileDown className="size-4" aria-hidden="true" />}
            onClick={() => void descargarPdf()}
          >
            PDF
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Input
            label="Fecha inicial"
            type="date"
            value={filtros.desde ?? ''}
            onChange={(evento) => setFiltros((previo) => ({ ...previo, desde: evento.target.value }))}
          />
          <Input
            label="Fecha final"
            type="date"
            value={filtros.hasta ?? ''}
            onChange={(evento) => setFiltros((previo) => ({ ...previo, hasta: evento.target.value }))}
          />
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-ink-700">Estado</span>
            <Select
              className={CONTROL}
              value={filtros.estado ?? 'todos'}
              onChange={(evento) => setFiltros((previo) => ({ ...previo, estado: evento.target.value }))}
            >
              {ESTADOS.map((estado) => (
                <option key={estado.valor} value={estado.valor}>
                  {estado.etiqueta}
                </option>
              ))}
            </Select>
          </label>
          <div className="flex items-end">
            <Button
              size="sm"
              className="w-full"
              disabled={consulta.isFetching}
              loading={consulta.isFetching}
              leftIcon={<ReceiptText className="size-4" aria-hidden="true" />}
              onClick={() => setAplicados(filtros)}
            >
              Consultar
            </Button>
          </div>
        </div>

        {errorPdf ? (
          <Alert variant="error" title="No se pudo generar el PDF">
            {errorPdf}
          </Alert>
        ) : null}

        {consulta.isLoading ? <Skeleton className="h-72 w-full" /> : null}

        {consulta.isError ? (
          <Alert variant="error" title="No se pudo leer el reporte">
            {consulta.error instanceof ApiError ? consulta.error.message : 'Inténtalo de nuevo.'}
          </Alert>
        ) : null}

        {datos ? (
          <>
            <div className="grid gap-3 sm:grid-cols-3">
              {[
                { etiqueta: 'Total facturado', valor: totales.facturado, icono: ReceiptText, tono: 'text-brand-600' },
                { etiqueta: 'Total abonado', valor: totales.abonado, icono: HandCoins, tono: 'text-emerald-600' },
                { etiqueta: 'Total pendiente', valor: totales.pendiente, icono: Hourglass, tono: 'text-rose-600' },
              ].map((tarjeta) => (
                <Card key={tarjeta.etiqueta}>
                  <CardContent className="flex items-center justify-between p-4">
                    <div>
                      <p className="text-xs font-semibold tracking-wide text-ink-500 uppercase">
                        {tarjeta.etiqueta}
                      </p>
                      <p className="text-lg font-bold text-ink-900">{monto(tarjeta.valor)}</p>
                      <p className="text-xs text-ink-500">
                        {datos.resumen.facturas} factura(s) de {datos.resumen.clientes} cliente(s)
                      </p>
                    </div>
                    <tarjeta.icono className={`size-7 ${tarjeta.tono}`} aria-hidden="true" />
                  </CardContent>
                </Card>
              ))}
            </div>

            {datos.resumen.vencido > 0 ? (
              <Alert variant="warning" title="Facturas vencidas">
                <span className={ICONO_EN_ALERTA}>
                  <Hourglass className="size-4" aria-hidden="true" />
                  {monto(datos.resumen.vencido)} pendiente de facturas con el vencimiento pasado.
                </span>
              </Alert>
            ) : null}

            {datos.clientes.length === 0 ? (
              <p className="text-sm text-ink-500">
                No hay facturas a crédito que cumplan el filtro en el rango.
              </p>
            ) : (
              <div className="max-h-[46vh] overflow-auto rounded-card border border-ink-100">
                <table className="w-full border-collapse text-[13px]">
                  <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                    <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                      <th className="px-3 py-2 whitespace-nowrap">Emisión</th>
                      <th className="px-3 py-2 whitespace-nowrap">Vencimiento</th>
                      <th className="px-3 py-2 whitespace-nowrap">Factura</th>
                      <th className="px-3 py-2">Información</th>
                      <th className="px-3 py-2 text-right whitespace-nowrap">Total</th>
                      <th className="px-3 py-2 text-right whitespace-nowrap">Retención</th>
                      <th className="px-3 py-2 text-right whitespace-nowrap">Abonado</th>
                      <th className="px-3 py-2 text-right whitespace-nowrap">Pendiente</th>
                      <th className="px-3 py-2 text-right whitespace-nowrap">Vencidos</th>
                      <th className="px-3 py-2 whitespace-nowrap">Estado</th>
                    </tr>
                  </thead>
                  {datos.clientes.map((grupo) => (
                    <tbody key={grupo.numero_doc ?? grupo.cliente} className="border-t border-ink-100">
                      <tr className="bg-brand-50/50">
                        <td className="px-3 py-2 font-semibold text-ink-800" colSpan={4}>
                          <span className="flex items-center gap-1.5">
                            <BadgeDollarSign className="size-3.5 text-brand-500" aria-hidden="true" />
                            {grupo.cliente ?? `Cliente ${grupo.numero_doc ?? ''}`}
                            <span className="font-normal text-ink-500">({grupo.numero_doc ?? '—'})</span>
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right font-semibold whitespace-nowrap">
                          {monto(grupo.facturado)}
                        </td>
                        <td className="px-3 py-2 text-right font-semibold whitespace-nowrap">
                          {monto(grupo.retencion)}
                        </td>
                        <td className="px-3 py-2 text-right font-semibold whitespace-nowrap">
                          {monto(grupo.abonado)}
                        </td>
                        <td className="px-3 py-2 text-right font-semibold whitespace-nowrap">
                          {monto(grupo.pendiente)}
                        </td>
                        <td className="px-3 py-2 text-right font-semibold whitespace-nowrap">
                          {monto(grupo.vencido)}
                        </td>
                        <td className="px-3 py-2 text-xs text-ink-500">
                          {grupo.facturas.length} factura(s)
                        </td>
                      </tr>

                      {grupo.facturas.map((factura) => (
                        <tr key={factura.id} className="border-t border-ink-100/70">
                          <td className="px-3 py-1.5 whitespace-nowrap">{fechaCorta(factura.fecha_emision)}</td>
                          <td className="px-3 py-1.5 whitespace-nowrap">
                            {fechaCorta(factura.fecha_vencimiento)}
                            {factura.dias_vencidos > 0 ? (
                              <span className="ml-1 text-xs text-rose-600">({factura.dias_vencidos} d)</span>
                            ) : null}
                          </td>
                          <td className="px-3 py-1.5 whitespace-nowrap">{factura.comprobante}</td>
                          <td className="max-w-[240px] truncate px-3 py-1.5" title={factura.observaciones ?? ''}>
                            {factura.observaciones ?? '—'}
                          </td>
                          <td className="px-3 py-1.5 text-right whitespace-nowrap">{monto(factura.total)}</td>
                          <td className="px-3 py-1.5 text-right whitespace-nowrap">{monto(factura.retencion)}</td>
                          <td className="px-3 py-1.5 text-right whitespace-nowrap">{monto(factura.pagado)}</td>
                          <td className="px-3 py-1.5 text-right whitespace-nowrap">{monto(factura.saldo)}</td>
                          <td className="px-3 py-1.5 text-right whitespace-nowrap">
                            {factura.vencido > 0 ? monto(factura.vencido) : '—'}
                          </td>
                          <td className="px-3 py-1.5">
                            <Badge variant={TONOS[factura.estado]}>{factura.estado}</Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  ))}
                  <tfoot className="sticky bottom-0 bg-ink-50/90">
                    <tr className="border-t border-ink-100 font-semibold text-ink-800">
                      <td className="px-3 py-2" colSpan={4}>
                        Totales del rango
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">{monto(datos.resumen.facturado)}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">{monto(datos.resumen.retencion)}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">{monto(datos.resumen.abonado)}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">{monto(datos.resumen.pendiente)}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">{monto(datos.resumen.vencido)}</td>
                      <td className="px-3 py-2 text-xs text-ink-500">Filtro: {datos.estado}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}

            <Alert variant="info" title="De dónde salen las facturas">
              <span className={ICONO_EN_ALERTA}>
                <ReceiptText className="size-4" aria-hidden="true" />
                Son las facturas a crédito (`condicion_pago != 'CONTADO'`, sin las pagadas por
                adelantado, sin anuladas y sin notas de crédito) con su `pending_amount` y su
                `paidout`, más el estado de cada comprobante en SUNAT. Los estados con «por vencer»
                usan {datos.dias_por_vencer} días desde hoy.
              </span>
            </Alert>
          </>
        ) : null}
      </div>
    </Dialog>
  );
}
